import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Newspaper, Package, Briefcase, Mail, Users, 
  Image, MessageSquare, LogOut, Menu, X, ChevronRight, Plus,
  Edit, Trash2, Eye, EyeOff, Loader2, Send, Settings, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import api, { statsAPI, newsAPI, productsAPI, servicesAPI, newsletterAPI, bannersAPI, contactAPI, brevoAPI } from '../../lib/api';
import { formatDateShort, formatPrice, resolveImageUrl } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import RichTextEditor from '../../components/common/RichTextEditor';
import ImageUpload from '../../components/common/ImageUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Toaster } from '../../components/ui/sonner';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Newspaper, label: 'Notícias', path: '/admin/news' },
  { icon: Package, label: 'Produtos', path: '/admin/products' },
  { icon: Briefcase, label: 'Serviços', path: '/admin/services' },
  { icon: Users, label: 'Newsletter', path: '/admin/newsletter' },
  { icon: Send, label: 'Enviar Newsletter', path: '/admin/newsletter/enviar' },
  { icon: Settings, label: 'Configurações Brevo', path: '/admin/newsletter/configuracoes' },
  { icon: Download, label: 'Leads (Downloads)', path: '/admin/leads' },
  { icon: MessageSquare, label: 'Mensagens', path: '/admin/contacts' },
];

const newsCategories = [
  'Vistos',
  'AIMA',
  'Cidadania',
  'Custo de Vida',
  'Moradia',
  'Cidades',
  'Saúde',
  'Trabalho',
  'Empreendedorismo',
  'Notícias',
  'Curiosidades',
];
const productCategories = ['ebook', 'guide', 'course'];
const serviceCategories = ['consultoria', 'advocacia', 'contabilidade', 'traduções', 'seguros'];

// Admin Layout Component
const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#E9E5D6]">
      {/* Mobile Header */}
      <header className="lg:hidden bg-[#0F5A34] text-white p-4 flex items-center justify-between">
        <Link to="/" className="font-['Playfair_Display'] text-xl font-bold">
          Portugal <span className="text-[#DFAF3A]">à Vista</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0F5A34] text-white 
          transform transition-transform duration-300 lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-white/10 hidden lg:block">
            <Link to="/" className="font-['Playfair_Display'] text-xl font-bold">
              Portugal <span className="text-[#DFAF3A]">à Vista</span>
            </Link>
            <p className="text-white/60 text-xs mt-1 font-['Inter']">Painel Administrativo</p>
          </div>

          <nav className="p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm transition-colors
                    font-['Montserrat'] text-sm
                    ${isActive 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <Icon size={18} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="font-['Montserrat'] text-sm font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-['Montserrat'] text-sm font-medium truncate">{user?.name}</p>
                <p className="text-white/60 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-white/30 text-white hover:bg-white/10"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <Toaster position="top-right" />
    </div>
  );
};

// Dashboard Page
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await statsAPI.getAdmin();
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { label: 'Notícias', value: stats?.news || 0, icon: Newspaper, color: 'bg-[#0F5A34]' },
    { label: 'Produtos', value: stats?.products || 0, icon: Package, color: 'bg-[#DFAF3A]' },
    { label: 'Serviços', value: stats?.services || 0, icon: Briefcase, color: 'bg-[#0F5A34]' },
    { label: 'Inscritos', value: stats?.subscribers || 0, icon: Users, color: 'bg-[#E53935]' },
    { label: 'Mensagens', value: stats?.unread_contacts || 0, icon: MessageSquare, color: 'bg-[#0F5A34]' },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Dashboard</h1>
        <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">Visão geral do portal</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
              <div className={`w-10 h-10 rounded-sm ${stat.color} flex items-center justify-center mb-3`}>
                <Icon size={20} className="text-white" />
              </div>
              <p className="font-['Montserrat'] text-2xl font-bold text-[#1A1A1A]">{stat.value}</p>
              <p className="text-[#5C5C5C] font-['Inter'] text-sm">{stat.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-4">
            Ações Rápidas
          </h3>
          <div className="space-y-3">
            <Link to="/admin/news">
              <Button className="w-full justify-start bg-[#0F5A34] hover:bg-[#0A3F24]">
                <Plus size={16} className="mr-2" />
                Criar Nova Notícia
              </Button>
            </Link>
            <Link to="/admin/products">
              <Button variant="outline" className="w-full justify-start border-[#0F5A34] text-[#0F5A34]">
                <Plus size={16} className="mr-2" />
                Adicionar Produto
              </Button>
            </Link>
            <Link to="/admin/services">
              <Button variant="outline" className="w-full justify-start border-[#0F5A34] text-[#0F5A34]">
                <Plus size={16} className="mr-2" />
                Adicionar Serviço
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-sm border border-[#D6D2C4]">
          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1A1A1A] mb-4">
            Links Úteis
          </h3>
          <div className="space-y-3">
            <Link to="/" target="_blank">
              <Button variant="ghost" className="w-full justify-start text-[#5C5C5C] hover:text-[#0F5A34]">
                <Eye size={16} className="mr-2" />
                Ver Site Público
              </Button>
            </Link>
            <Link to="/admin/newsletter">
              <Button variant="ghost" className="w-full justify-start text-[#5C5C5C] hover:text-[#0F5A34]">
                <Mail size={16} className="mr-2" />
                Lista de Inscritos
              </Button>
            </Link>
            <Link to="/admin/contacts">
              <Button variant="ghost" className="w-full justify-start text-[#5C5C5C] hover:text-[#0F5A34]">
                <MessageSquare size={16} className="mr-2" />
                Ver Mensagens
                {stats?.unread_contacts > 0 && (
                  <Badge className="ml-auto bg-[#E53935] text-white">{stats.unread_contacts}</Badge>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// News Management Page
export const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    category: 'Notícias',
    image_url: '',
    is_featured: false,
    is_breaking: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await newsAPI.getAllAdmin();
      setNews(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notícias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingNews) {
        await newsAPI.update(editingNews.id, formData);
        toast.success('Notícia atualizada!');
      } else {
        await newsAPI.create(formData);
        toast.success('Notícia criada!');
      }
      setDialogOpen(false);
      resetForm();
      loadNews();
    } catch (error) {
      toast.error('Erro ao salvar notícia');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      subtitle: item.subtitle || '',
      content: item.content,
      category: item.category,
      image_url: item.image_url || '',
      is_featured: item.is_featured,
      is_breaking: item.is_breaking,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta notícia?')) return;
    try {
      await newsAPI.delete(id);
      toast.success('Notícia excluída!');
      loadNews();
    } catch (error) {
      toast.error('Erro ao excluir notícia');
    }
  };

  const resetForm = () => {
    setEditingNews(null);
    setFormData({
      title: '',
      subtitle: '',
      content: '',
      category: 'Notícias',
      image_url: '',
      is_featured: false,
      is_breaking: false,
    });
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Notícias</h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">Gerencie as notícias do portal</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5A34] hover:bg-[#0A3F24]" data-testid="create-news-btn">
              <Plus size={16} className="mr-2" />
              Nova Notícia
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">
                {editingNews ? 'Editar Notícia' : 'Nova Notícia'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="news-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtítulo</Label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {newsCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conteúdo</Label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  placeholder="Escreva o conteúdo da notícia..."
                />
              </div>
              <div className="space-y-2">
                <Label>Imagem de Destaque</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  />
                  <span className="text-sm">Destaque</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_breaking}
                    onChange={(e) => setFormData({ ...formData, is_breaking: e.target.checked })}
                  />
                  <span className="text-sm">Urgente</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#0F5A34] hover:bg-[#0A3F24]" data-testid="save-news-btn">
                  {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                  {editingNews ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-[#D6D2C4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="news-table">
              <thead className="bg-[#E9E5D6]">
                <tr>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Título</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Categoria</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Status</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Data</th>
                  <th className="text-right p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {news.map((item) => (
                  <tr key={item.id} className="border-t border-[#D6D2C4]">
                    <td className="p-4">
                      <p className="font-['Inter'] text-sm text-[#1A1A1A] line-clamp-1">{item.title}</p>
                    </td>
                    <td className="p-4">
                      <Badge className="bg-[#0F5A34] text-white text-xs">{item.category}</Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        {item.is_featured && <Badge className="bg-[#DFAF3A] text-[#1A1A1A] text-xs">Destaque</Badge>}
                        {item.is_breaking && <Badge className="bg-[#E53935] text-white text-xs">Urgente</Badge>}
                      </div>
                    </td>
                    <td className="p-4 text-[#5C5C5C] font-['Inter'] text-sm">{formatDateShort(item.created_at)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(item)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Products Management Page
export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'EUR',
    image_url: '',
    external_link: '',
    category: 'ebook',
    is_featured: false,
    is_free: false,
    is_lead_gated: false,
    file_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAllAdmin();
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, formData);
        toast.success('Produto atualizado!');
      } else {
        await productsAPI.create(formData);
        toast.success('Produto criado!');
      }
      setDialogOpen(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingProduct(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      currency: item.currency,
      image_url: item.image_url || '',
      external_link: item.external_link || '',
      category: item.category,
      is_featured: item.is_featured,
      is_free: item.is_free || false,
      is_lead_gated: item.is_lead_gated || false,
      file_url: item.file_url || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Produto excluído!');
      loadProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      currency: 'EUR',
      image_url: '',
      external_link: '',
      category: 'ebook',
      is_featured: false,
      is_free: false,
      is_lead_gated: false,
      file_url: '',
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Arquivo demasiado grande (máx. 30MB)');
      return;
    }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, file_url: res.data.url }));
      toast.success('Arquivo enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha no upload');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Produtos</h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">Gerencie os produtos digitais</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5A34] hover:bg-[#0A3F24]" data-testid="create-product-btn">
              <Plus size={16} className="mr-2" />
              Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preço</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    disabled={formData.is_free}
                    required={!formData.is_free}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Link Externo (loja, checkout, etc.)</Label>
                <Input
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  placeholder="https://... (opcional se for grátis)"
                />
              </div>
              <div className="space-y-2">
                <Label>Imagem (capa)</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
              </div>

              <div className="border-t border-[#D6D2C4] pt-4 space-y-3">
                <p className="text-xs font-medium tracking-wider uppercase text-[#5C5C5C]">Produto grátis / Captação de Leads</p>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_free}
                    onChange={(e) => setFormData({ ...formData, is_free: e.target.checked, price: e.target.checked ? 0 : formData.price })}
                  />
                  <span className="text-sm">Produto gratuito</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_lead_gated}
                    onChange={(e) => setFormData({ ...formData, is_lead_gated: e.target.checked })}
                  />
                  <span className="text-sm">Requer cadastro (nome + email) para download</span>
                </label>

                {(formData.is_free || formData.is_lead_gated) && (
                  <div className="space-y-2">
                    <Label>Arquivo para download (PDF, ePub, etc.)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={formData.file_url}
                        onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                        placeholder="/api/files/..."
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('product-file-input').click()}
                        disabled={uploadingFile}
                        className="border-[#0F5A34] text-[#0F5A34]"
                      >
                        {uploadingFile ? <Loader2 size={14} className="animate-spin" /> : 'Upload'}
                      </Button>
                      <input
                        id="product-file-input"
                        type="file"
                        accept=".pdf,.epub,.zip,.doc,.docx,.xls,.xlsx"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </div>
                    {formData.file_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${formData.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#0F5A34] underline"
                      >
                        Pré-visualizar arquivo
                      </a>
                    )}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                />
                <span className="text-sm">Destaque</span>
              </label>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#0F5A34] hover:bg-[#0A3F24]">
                  {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                  {editingProduct ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.id} className="bg-white p-4 rounded-sm border border-[#D6D2C4]">
              {product.image_url && (
                <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-32 object-cover rounded-sm mb-3" />
              )}
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] mb-1">{product.name}</h3>
              <p className="text-[#5C5C5C] font-['Inter'] text-sm mb-2 line-clamp-2">{product.description}</p>
              <p className={`font-['Montserrat'] text-lg font-bold mb-3 ${product.is_free ? 'text-[#E53935]' : 'text-[#0F5A34]'}`}>
                {product.is_free ? 'GRÁTIS' : formatPrice(product.price)}
                {product.is_lead_gated && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#DFAF3A] text-[#1A1A1A] px-1.5 py-0.5 rounded-sm align-middle">
                    Lead
                  </span>
                )}
              </p>
              {(product.is_free || product.is_lead_gated) && (
                <div
                  className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-[#FFFBEE] border border-[#DFAF3A]/40 rounded-sm"
                  title="Quantidade de downloads do ficheiro"
                  data-testid={`product-downloads-${product.id}`}
                >
                  <Download size={14} className="text-[#0F5A34]" />
                  <span className="text-xs font-['Inter'] text-[#1A1A1A]">
                    <strong className="font-['Playfair_Display'] text-base text-[#0F5A34]">{product.download_count || 0}</strong>
                    <span className="ml-1 text-[#5C5C5C]">download(s)</span>
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>
                  <Edit size={14} className="mr-1" /> Editar
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(product.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// Services Management Page
export const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'consultoria',
    website_url: '',
    phone: '',
    email: '',
    image_url: '',
    is_featured: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAllAdmin();
      setServices(response.data);
    } catch (error) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingService) {
        await servicesAPI.update(editingService.id, formData);
        toast.success('Serviço atualizado!');
      } else {
        await servicesAPI.create(formData);
        toast.success('Serviço criado!');
      }
      setDialogOpen(false);
      resetForm();
      loadServices();
    } catch (error) {
      toast.error('Erro ao salvar serviço');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingService(item);
    setFormData({
      name: item.name,
      description: item.description,
      category: item.category,
      website_url: item.website_url || '',
      phone: item.phone || '',
      email: item.email || '',
      image_url: item.image_url || '',
      is_featured: item.is_featured,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este serviço?')) return;
    try {
      await servicesAPI.delete(id);
      toast.success('Serviço excluído!');
      loadServices();
    } catch (error) {
      toast.error('Erro ao excluir serviço');
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      category: 'consultoria',
      website_url: '',
      phone: '',
      email: '',
      image_url: '',
      is_featured: false,
    });
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Serviços</h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">Gerencie os serviços recomendados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5A34] hover:bg-[#0A3F24]" data-testid="create-service-btn">
              <Plus size={16} className="mr-2" />
              Novo Serviço
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">
                {editingService ? 'Editar Serviço' : 'Novo Serviço'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Imagem</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                />
                <span className="text-sm">Recomendado</span>
              </label>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#0F5A34] hover:bg-[#0A3F24]">
                  {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                  {editingService ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service) => (
            <div key={service.id} className="bg-white p-4 rounded-sm border border-[#D6D2C4]">
              {service.image_url && (
                <img src={resolveImageUrl(service.image_url)} alt={service.name} className="w-full h-32 object-cover rounded-sm mb-3" />
              )}
              <Badge className="bg-[#0F5A34] text-white text-xs mb-2">{service.category}</Badge>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A] mb-1">{service.name}</h3>
              <p className="text-[#5C5C5C] font-['Inter'] text-sm mb-3 line-clamp-2">{service.description}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(service)}>
                  <Edit size={14} className="mr-1" /> Editar
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(service.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// Newsletter Page
export const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      const response = await newsletterAPI.getAll();
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar inscritos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Newsletter</h1>
        <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">{subscribers.length} inscritos</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-[#D6D2C4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#E9E5D6]">
                <tr>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Email</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Nome</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Data</th>
                  <th className="text-left p-4 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5C5C5C]">Status</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub) => (
                  <tr key={sub.id} className="border-t border-[#D6D2C4]">
                    <td className="p-4 font-['Inter'] text-sm text-[#1A1A1A]">{sub.email}</td>
                    <td className="p-4 font-['Inter'] text-sm text-[#5C5C5C]">{sub.name || '-'}</td>
                    <td className="p-4 font-['Inter'] text-sm text-[#5C5C5C]">{formatDateShort(sub.subscribed_at)}</td>
                    <td className="p-4">
                      <Badge className={sub.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}>
                        {sub.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Banners Page
export const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    position: 'sidebar',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const response = await bannersAPI.getAllAdmin();
      setBanners(response.data);
    } catch (error) {
      toast.error('Erro ao carregar banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await bannersAPI.create(formData);
      toast.success('Banner criado!');
      setDialogOpen(false);
      setFormData({ title: '', image_url: '', link_url: '', position: 'sidebar', is_active: true });
      loadBanners();
    } catch (error) {
      toast.error('Erro ao criar banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este banner?')) return;
    try {
      await bannersAPI.delete(id);
      toast.success('Banner excluído!');
      loadBanners();
    } catch (error) {
      toast.error('Erro ao excluir banner');
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Banners</h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">Gerencie os banners publicitários</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5A34] hover:bg-[#0A3F24]">
              <Plus size={16} className="mr-2" />
              Novo Banner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">Novo Banner</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>URL da Imagem</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Link de Destino</Label>
                <Input
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://..."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Posição</Label>
                <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="header">Header</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                    <SelectItem value="article">Artigo</SelectItem>
                    <SelectItem value="footer">Footer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#0F5A34] hover:bg-[#0A3F24]">
                  {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                  Criar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : banners.length === 0 ? (
        <p className="text-[#5C5C5C] text-center py-12">Nenhum banner cadastrado</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-white p-4 rounded-sm border border-[#D6D2C4]">
              <img src={banner.image_url} alt={banner.title} className="w-full h-32 object-cover rounded-sm mb-3" />
              <h3 className="font-['Inter'] text-sm font-medium text-[#1A1A1A] mb-1">{banner.title}</h3>
              <Badge className="bg-[#0F5A34] text-white text-xs mb-3">{banner.position}</Badge>
              <Button size="sm" variant="outline" className="text-red-600 w-full" onClick={() => handleDelete(banner.id)}>
                <Trash2 size={14} className="mr-1" /> Excluir
              </Button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// Contacts Page
export const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const response = await contactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      loadContacts();
    } catch (error) {
      toast.error('Erro ao marcar como lida');
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Mensagens</h1>
        <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">{contacts.filter(c => !c.is_read).length} não lidas</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : contacts.length === 0 ? (
        <p className="text-[#5C5C5C] text-center py-12">Nenhuma mensagem recebida</p>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className={`bg-white p-6 rounded-sm border ${contact.is_read ? 'border-[#D6D2C4]' : 'border-[#0F5A34] border-2'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#1A1A1A]">{contact.subject}</h3>
                  <p className="text-[#5C5C5C] font-['Inter'] text-sm">
                    {contact.name} • {contact.email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!contact.is_read && (
                    <Badge className="bg-[#E53935] text-white">Nova</Badge>
                  )}
                  <span className="text-[#5C5C5C] text-xs">{formatDateShort(contact.created_at)}</span>
                </div>
              </div>
              <p className="text-[#1A1A1A] font-['Inter'] text-sm mb-4">{contact.message}</p>
              <div className="flex gap-2">
                <a href={`mailto:${contact.email}`}>
                  <Button size="sm" className="bg-[#0F5A34] hover:bg-[#0A3F24]">
                    <Mail size={14} className="mr-1" /> Responder
                  </Button>
                </a>
                {!contact.is_read && (
                  <Button size="sm" variant="outline" onClick={() => markAsRead(contact.id)}>
                    Marcar como lida
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

// ============ AdminNewsletterSettings ============

export const AdminNewsletterSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await brevoAPI.getSettings();
      setSettings(res.data);
    } catch (e) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await brevoAPI.updateSettings({
        sender_email: settings.sender_email || '',
        sender_name: settings.sender_name || 'Portugal à Vista',
        list_id: settings.list_id ? Number(settings.list_id) : null,
        welcome_subject: settings.welcome_subject || '',
        welcome_html: settings.welcome_html || '',
        send_welcome_email: !!settings.send_welcome_email,
      });
      setSettings(res.data);
      toast.success('Configurações guardadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Configurações Brevo</h1>
        <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">
          Configure o remetente e a lista da sua conta Brevo para envio de newsletters.
        </p>
      </div>

      <div className="bg-white rounded-sm border border-[#D6D2C4] p-6 max-w-3xl space-y-5">
        <div className={`px-3 py-2 rounded-sm text-sm font-['Inter'] ${settings.api_key_configured ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="brevo-key-status">
          {settings.api_key_configured
            ? 'Chave API Brevo configurada no servidor.'
            : 'BREVO_API_KEY ausente no servidor. Adicione em backend/.env.'}
        </div>

        <div className="bg-[#FFF8E1] border border-[#DFAF3A] text-[#1A1A1A] px-3 py-2 rounded-sm text-xs font-['Inter']">
          <strong>Dica:</strong> Se receber erro de "IP não autorizado" do Brevo, vá em{' '}
          <a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noreferrer" className="underline">
            app.brevo.com/security/authorised_ips
          </a>{' '}
          e adicione o IP do servidor (ou desative a restrição).
        </div>

        <div className="space-y-2">
          <Label>Email do remetente *</Label>
          <Input
            type="email"
            value={settings.sender_email || ''}
            onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
            placeholder="newsletter@seudominio.com"
            data-testid="brevo-sender-email"
          />
          <p className="text-xs text-[#5C5C5C]">Deve estar verificado no Brevo (Senders, Domains & Dedicated IPs).</p>
        </div>

        <div className="space-y-2">
          <Label>Nome do remetente</Label>
          <Input
            value={settings.sender_name || ''}
            onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
            data-testid="brevo-sender-name"
          />
        </div>

        <div className="space-y-2">
          <Label>ID da Lista (opcional)</Label>
          <Input
            type="number"
            value={settings.list_id ?? ''}
            onChange={(e) => setSettings({ ...settings, list_id: e.target.value })}
            placeholder="Ex: 42"
            data-testid="brevo-list-id"
          />
          <p className="text-xs text-[#5C5C5C]">Brevo → Contactos → Listas → o número visível no URL.</p>
        </div>

        <div className="border-t border-[#D6D2C4] pt-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.send_welcome_email}
              onChange={(e) => setSettings({ ...settings, send_welcome_email: e.target.checked })}
              data-testid="brevo-send-welcome"
            />
            <span className="text-sm font-['Inter']">Enviar email de boas-vindas aos novos inscritos</span>
          </label>

          {settings.send_welcome_email && (
            <>
              <div className="space-y-2">
                <Label>Assunto do email de boas-vindas</Label>
                <Input
                  value={settings.welcome_subject || ''}
                  onChange={(e) => setSettings({ ...settings, welcome_subject: e.target.value })}
                  data-testid="brevo-welcome-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Conteúdo (HTML)</Label>
                <RichTextEditor
                  value={settings.welcome_html || ''}
                  onChange={(html) => setSettings({ ...settings, welcome_html: html })}
                />
              </div>
            </>
          )}
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#0F5A34] hover:bg-[#0c4628] text-white"
          data-testid="brevo-save-btn"
        >
          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          Guardar configurações
        </Button>
      </div>
    </AdminLayout>
  );
};

// ============ AdminNewsletterBroadcast ============

export const AdminNewsletterBroadcast = () => {
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    newsletterAPI.getAll().then(r => setCount(r.data.filter(s => s.is_active).length)).catch(() => {});
  }, []);

  const send = async () => {
    if (!subject.trim() || !html.trim()) {
      toast.error('Preencha o assunto e o conteúdo');
      return;
    }
    if (!window.confirm(`Enviar para ${count ?? '?'} subscritores ativos?`)) return;
    setSending(true);
    try {
      const res = await brevoAPI.broadcast({ subject, html_content: html });
      const { total, batches, failed } = res.data;
      toast.success(`Enviado para ${total} subscritores em ${batches} lote(s). Falhas: ${failed}.`);
      setSubject('');
      setHtml('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Enviar Newsletter</h1>
        <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">
          {count !== null ? `${count} subscritores ativos` : 'A carregar inscritos...'}
        </p>
      </div>

      <div className="bg-white rounded-sm border border-[#D6D2C4] p-6 max-w-4xl space-y-5">
        <div className="space-y-2">
          <Label>Assunto *</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Título do email"
            data-testid="broadcast-subject"
          />
        </div>
        <div className="space-y-2">
          <Label>Conteúdo *</Label>
          <RichTextEditor value={html} onChange={setHtml} />
        </div>
        <Button
          onClick={send}
          disabled={sending}
          className="bg-[#E53935] hover:bg-red-700 text-white"
          data-testid="broadcast-send-btn"
        >
          {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
          Enviar para todos os inscritos
        </Button>
      </div>
    </AdminLayout>
  );
};

// ============ AdminLeads ============

export const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/leads')
      .then((r) => setLeads(r.data))
      .catch(() => toast.error('Erro ao carregar leads'))
      .finally(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    const header = ['Nome', 'Email', 'Produto', 'Newsletter?', 'Data'];
    const rows = leads.map((l) => [
      l.name, l.email, l.product_name,
      l.subscribed_to_newsletter ? 'Sim' : 'Não',
      new Date(l.created_at).toLocaleString('pt-PT'),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A1A1A]">Leads (Downloads)</h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm mt-1">
            {leads.length} cadastro(s) capturados via download gratuito.
          </p>
        </div>
        <Button onClick={exportCSV} className="bg-[#0F5A34] hover:bg-[#0c4628]" disabled={!leads.length}>
          <Download size={16} className="mr-2" /> Exportar CSV
        </Button>
      </div>

      <div className="bg-white rounded-sm border border-[#D6D2C4] overflow-hidden" data-testid="leads-table">
        <table className="w-full">
          <thead className="bg-[#0F5A34] text-white">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-['Montserrat'] uppercase tracking-wider">Nome</th>
              <th className="text-left px-4 py-3 text-xs font-['Montserrat'] uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-3 text-xs font-['Montserrat'] uppercase tracking-wider">Produto</th>
              <th className="text-left px-4 py-3 text-xs font-['Montserrat'] uppercase tracking-wider">Newsletter</th>
              <th className="text-left px-4 py-3 text-xs font-['Montserrat'] uppercase tracking-wider">Data</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5C5C5C]">Nenhum lead capturado ainda.</td></tr>
            ) : leads.map((l) => (
              <tr key={l.id} className="border-t border-[#E9E5D6] hover:bg-[#FFFBEE]">
                <td className="px-4 py-3 text-sm font-medium text-[#1A1A1A]">{l.name}</td>
                <td className="px-4 py-3 text-sm text-[#0F5A34]">{l.email}</td>
                <td className="px-4 py-3 text-sm text-[#5C5C5C]">{l.product_name}</td>
                <td className="px-4 py-3 text-sm">
                  {l.subscribed_to_newsletter ? (
                    <span className="text-[#0F5A34] font-medium">Sim</span>
                  ) : (
                    <span className="text-[#5C5C5C]">Não</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C5C5C]">
                  {new Date(l.created_at).toLocaleString('pt-PT')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
